"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { scrollFx } from "@/modules/visual/scrollFx";
import DotsPattern from "../CSSPatterns/DotsPattern/DotsPattern";
import { useTranslationHomePage } from "./home.hook";

function getYouTubeId(urlOrId: string) {
  try {
    if (!urlOrId.includes("http")) return urlOrId;
    const u = new URL(urlOrId);
    const v = u.searchParams.get("v");
    if (v) return v;
    if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "");
    const parts = u.pathname.split("/").filter(Boolean);
    const embedIdx = parts.indexOf("embed");
    if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];
    return urlOrId;
  } catch {
    return urlOrId;
  }
}

// Tiny inline icons so you don’t need extra deps
function IconVolumeOff(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M11 5 6.8 8.5H3v7h3.8L11 19V5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M16 9l5 6M21 9l-5 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconVolumeOn(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M11 5 6.8 8.5H3v7h3.8L11 19V5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M15.5 9.5a3.8 3.8 0 0 1 0 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M17.8 7.2a7 7 0 0 1 0 9.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

type VideoShowcaseProps = {
  title?: string;
  subtitle?: string;
  youtubeUrl?: string;
};

export default function VideoShowcase({
  youtubeUrl = "https://www.youtube.com/watch?v=W7QA7l7jadM",
}: VideoShowcaseProps) {
  const {
    videoShowCaseTitle,
    videoShowCaseTitleSubTitle,
    videoShowCaseKeyHighlight,
    videoShowCaseTipText,
  } = useTranslationHomePage();

  const id = useMemo(() => getYouTubeId(youtubeUrl), [youtubeUrl]);

  const mountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);

  const [isMuted, setIsMuted] = useState(true);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    function loadYouTubeAPI() {
      return new Promise<void>((resolve) => {
        // already loaded
        if (window.YT?.Player) return resolve();

        // already injecting
        const existing = document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]',
        );
        if (existing) {
          const prev = window.onYouTubeIframeAPIReady;
          window.onYouTubeIframeAPIReady = () => {
            prev?.();
            resolve();
          };
          return;
        }

        const s = document.createElement("script");
        s.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(s);
        window.onYouTubeIframeAPIReady = () => resolve();
      });
    }

    async function init() {
      if (!mountRef.current) return;

      await loadYouTubeAPI();
      if (cancelled) return;

      // Clean any previous player instance
      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }

      playerRef.current = new window.YT.Player(mountRef.current, {
        videoId: id,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          loop: 1,
          playlist: id, // needed for loop
          iv_load_policy: 3,
        },
        events: {
          onReady: (e: any) => {
            if (cancelled) return;
            try {
              e.target.mute();
              e.target.playVideo();
            } catch {}
            setIsMuted(true);
            setIsReady(true);
          },
        },
      });
    }

    init();

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {}
      playerRef.current = null;
    };
  }, [id]);

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;

    try {
      if (p.isMuted?.()) {
        p.unMute();
        setIsMuted(false);
      } else {
        p.mute();
        setIsMuted(true);
      }

      // If autoplay was blocked, a click often unlocks playback too:
      p.playVideo?.();
    } catch {}
  };

  return (
    <section className="relative overflow-hidden py-16 md:py-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-8 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="absolute left-[10%] top-40 h-[280px] w-[280px] rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute right-[8%] top-56 h-[260px] w-[260px] rounded-full bg-fuchsia-400/15 blur-3xl" />
      </div>

      <div
        className="relative mx-auto w-full max-w-6xl px-4 md:px-8"
        {...scrollFx({ trigger: "enter", effect: "fade" })}
      >
        <DotsPattern />

        <div className="mx-auto max-w-2xl text-center text-black">
          <h3 className="text-3xl md:text-4xl font-bold tracking-tight e">
            {videoShowCaseTitle}
          </h3>
          <p className="mt-3 text-base md:text-lg e/75">
            {videoShowCaseTitleSubTitle}
          </p>
        </div>

        <div className="relative mt-10 md:mt-14">
          <div className="rounded-3xl bg-gradient-to-br from-violet-500/60 via-blue-500/40 to-fuchsia-400/50 p-[1px] shadow-[0_30px_120px_-40px_rgba(0,0,0,0.85)]">
            <div className="rounded-3xl bg-zinc-950/55 backdrop-blur-xl ring-1 ring-white/10 overflow-hidden">
              <div className="flex items-center gap-3 border-b border-white/10 bg-white/5 px-5 py-3">
                <div className="flex gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-yellow-300/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-green-400/80" />
                </div>
                <div className="ml-2 flex-1">
                  <div className="mx-auto max-w-xl rounded-full bg-black/30 px-4 py-1 text-xs text-white/60 ring-1 ring-white/10">
                    Démo — Agent IA e-commerce
                  </div>
                </div>
                <span className="hidden sm:inline-flex rounded-full bg-white/5 px-3 py-1 text-xs text-white/70 ring-1 ring-white/10">
                  Autoplay · {isMuted ? "Muet" : "Son"}
                </span>
              </div>

              <div className="relative">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-white/5"
                />

                <div className="relative aspect-video w-full group">
                  {/* YT mounts here */}
                  <div ref={mountRef} className="h-full w-full" />

                  {/* Mute toggle */}
                  <button
                    type="button"
                    onClick={toggleMute}
                    disabled={!isReady}
                    className={[
                      "absolute right-4 top-4 z-10 inline-flex items-center gap-2 rounded-full",
                      "bg-white/10 px-4 py-2 text-sm text-white backdrop-blur-md",
                      "ring-1 ring-white/15 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.8)]",
                      "transition",
                      // 👇 hidden by default, appears on hover (desktop) + when focused (keyboard)
                      "opacity-0 translate-y-1 pointer-events-none",
                      "group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto",
                      "focus-visible:opacity-100 focus-visible:translate-y-0 focus-visible:pointer-events-auto",
                      // subtle hover on the button itself
                      "hover:bg-white/15 active:scale-[0.98]",
                      "disabled:opacity-0 disabled:pointer-events-none",
                    ].join(" ")}
                    aria-label={isMuted ? "Activer le son" : "Couper le son"}
                    title={isMuted ? "Activer le son" : "Couper le son"}
                  >
                    <span className="grid h-5 w-5 place-items-center">
                      {isMuted ? (
                        <IconVolumeOff className="h-5 w-5" />
                      ) : (
                        <IconVolumeOn className="h-5 w-5" />
                      )}
                    </span>
                    <span className="hidden sm:inline">
                      {isMuted ? "Activer le son" : "Couper le son"}
                    </span>
                  </button>
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-white/10 bg-white/5 px-5 py-3">
                  <div className="text-sm text-white/70">
                    <span className="font-semibold text-white">
                      {videoShowCaseKeyHighlight}
                    </span>{" "}
                    {videoShowCaseTipText}
                  </div>
                  <div className="hidden md:flex items-center gap-2 text-xs text-white/55">
                    <span className="rounded-full bg-white/5 px-3 py-1 ring-1 ring-white/10">
                      16:9
                    </span>
                    <span className="rounded-full bg-white/5 px-3 py-1 ring-1 ring-white/10">
                      Embedded
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DotsPattern right={180} />
        </div>
      </div>
    </section>
  );
}
